import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import { BulkEditAuthorActions, SlideAuthorActions } from '@medsurf/flat-actions';
import * as FlatStates from '../../internal';

export interface BulkEditAuthorStateModel {
  selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null;
  copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null;
  selectMediaSet: Set<string>;
  parentNodeId: string | null;
  selectNodeSet: Set<string>;
  selectPageSet: Set<string>;
  selectLayerSet: Set<string>;
  selectImageSet: Set<string>;
  selectGroupSet: Set<string>;
  selectAnnotationSet: Set<string>;
  selectQuestionSet: Set<string>;
  selectChoiceSet: Set<string>;
  selectPoiSet: Set<string>;
  copyMediaSet: Set<string>;
  copyNodeSet: Set<string>;
  copyPageSet: Set<string>;
  copyLayerSet: Set<string>;
  copyImageSet: Set<string>;
  copyGroupSet: Set<string>;
  copyAnnotationSet: Set<string>;
  copyQuestionSet: Set<string>;
  copyChoiceSet: Set<string>;
  copyPoiSet: Set<string>;
}

@State<BulkEditAuthorStateModel>({
  name: 'bulkEditAuthor',
  defaults: {
    selectType: null,
    copyType: null,
    selectMediaSet: new Set<string>(),
    parentNodeId: null,
    selectNodeSet: new Set<string>(),
    selectPageSet: new Set<string>(),
    selectLayerSet: new Set<string>(),
    selectImageSet: new Set<string>(),
    selectGroupSet: new Set<string>(),
    selectAnnotationSet: new Set<string>(),
    selectQuestionSet: new Set<string>(),
    selectChoiceSet: new Set<string>(),
    selectPoiSet: new Set<string>(),
    copyMediaSet: new Set<string>(),
    copyNodeSet: new Set<string>(),
    copyPageSet: new Set<string>(),
    copyLayerSet: new Set<string>(),
    copyImageSet: new Set<string>(),
    copyGroupSet: new Set<string>(),
    copyAnnotationSet: new Set<string>(),
    copyQuestionSet: new Set<string>(),
    copyChoiceSet: new Set<string>(),
    copyPoiSet: new Set<string>(),
  }
})
@Injectable()
export class BulkEditAuthorState {
  constructor(public store: Store) {
  }
  
  //<editor-fold desc="Selectors">
  
  @Selector([BulkEditAuthorState])
  static selectType$(state: BulkEditAuthorStateModel) {
    return state.selectType;
  }
  
  @Selector([BulkEditAuthorState])
  static copyType$(state: BulkEditAuthorStateModel) {
    return state.copyType;
  }

  //<editor-fold desc="Media">

  @Selector([BulkEditAuthorState])
  static mediaSet$(state: BulkEditAuthorStateModel) {
    return state.selectMediaSet;
  }
  
  @Selector([
    BulkEditAuthorState.mediaSet$,
    FlatStates.MediaEntityState.typedEntities$
  ])
  static medias$(mediaSet: Set<string>,
                 typedMedias: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType }) {
    return Array.from(mediaSet).reduce((arr, mediaId) => {
      const entity = typedMedias[mediaId];
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.MediaEntityModels.MediaEntityType[]);
  }
  
  @Selector([
    BulkEditAuthorState.medias$,
    FlatStates.MediaAuthorState.mediaUsedIn$
  ])
  static hasUsedIn$(medias: FlatModels.MediaEntityModels.MediaEntityType[],
                    mediasUsedIn: Record<string, FlatModels.MediaAuthorModels.MediaUsedInResultEntry[]>) {
    return !!medias.find((media: FlatModels.MediaEntityModels.MediaEntityType) => {
      return mediasUsedIn[media.id]?.length > 0 || false;
    });
  }

  @Selector([BulkEditAuthorState.mediaSet$])
  static hasBulkEditMedia$(mediaSet: Set<string>) {
    return (id: string) => {
      return mediaSet.has(id);
    }
  }

  //</editor-fold>
  
  //<editor-fold desc="Node">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static selectNodeSet$(state: BulkEditAuthorStateModel,
                        typedNodes: {[id: string]: FlatModels.NodeEntityModels.NodeEntityType}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY) {
      return Array.from(state.selectNodeSet).reduce((arr, node) => {
        if (typedNodes[node]) {
          arr.add(node);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectNodeSet$,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static pageIdsFromSelectNodeSet$(selectNodeSet: Set<string>,
                                   typedNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    return Array.from(selectNodeSet).reduce((arr, nodeId) => {
      const entity = typedNodes[nodeId];
      if (entity && !arr.has(entity.page)) {
        arr.add(entity.page);
      }
      return arr;
    }, new Set<string>()) as Set<string>;
  }

  @Selector([BulkEditAuthorState.selectNodeSet$])
  static hasBulkEditNode$(nodeSet: Set<string>) {
    return (id: string) => {
      return nodeSet.has(id);
    }
  }

  @Selector([BulkEditAuthorState.selectNodeSet$])
  static hasAllBulkEditNodes$(nodeSet: Set<string>) {
    return (ids: string[]) => {
      let checked = ids.length > 0;
      for (const id of ids) {
        if (!nodeSet.has(id)) {
          checked = false;
          break;
        }
      }
      return checked;
    };
  }
  
  @Selector([
    BulkEditAuthorState.selectNodeSet$,
    FlatStates.NodeEntityState.currentSelectedRouteElement$,
  ])
  static isNodeSelected$(nodeSet: Set<string>,
                         currentSelectedRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    return (id: string) => {
      if (nodeSet.size === 0) {
        return currentSelectedRouteElement?.id === id;
      } else {
        return nodeSet.has(id);
      }
    }
  }
  
  @Selector([
    BulkEditAuthorState.copyNodeSet$,
    FlatStates.NodeEntityState.typedRouteElements$,
    FlatStates.NodeEntityState.currentSelectedRouteElement$,
  ])
  static allowedCopyNodes$(copyNodeSet: Set<string>,
                           typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement},
                           currentRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    if (currentRouteElement) {
      return Array.from(copyNodeSet).reduce((arr, id) => {
        const routeElement = typedRouteElements[id];

        // TODO check depth here -> Project: No fixed node structure
        if (routeElement && routeElement.parentId !== currentRouteElement.id && routeElement.depth - 1 === currentRouteElement.depth) {
          arr.push(id);
        }

        return arr;
      }, [] as string[]);
    }
    return [];
  }
  
  @Selector([
    BulkEditAuthorState.allowedCopyNodes$,
    BulkEditAuthorState.copyNodeSet$
  ])
  static hasNotAllowedCopyNodes$(allowedCopyNodes: string[],
                                 copyNodeSet: Set<string>) {
    if (Array.isArray(allowedCopyNodes) && copyNodeSet) {
      for (const nodeId of copyNodeSet) {
        if (!allowedCopyNodes.includes(nodeId)) {
          return true;
        }
      }
    }
    return false;
  }
  
  @Selector([
    BulkEditAuthorState.selectNodeSet$,
    FlatStates.NodeEntityState.typedRouteElements$,
    FlatStates.NodeEntityState.currentSelectedRouteElement$,
  ])
  static allowedRemoveNodes$(selectNodeSet: Set<string>,
                             typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement},
                             currentRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    if (currentRouteElement) {
      return Array.from(selectNodeSet).reduce((arr, id) => {
        const routeElement = typedRouteElements[id];

        if (routeElement && routeElement.parentId === currentRouteElement.id) {
          arr.push(id);
        }

        return arr;
      }, [] as string[]);
    }
    return [];
  }
  
  @Selector([
    BulkEditAuthorState.allowedRemoveNodes$,
    BulkEditAuthorState.selectNodeSet$
  ])
  static hasNotAllowedRemoveNodes$(allowedRemoveNodes: string[],
                                   selectNodeSet: Set<string>) {
    if (Array.isArray(allowedRemoveNodes) && selectNodeSet) {
      for (const nodeId of selectNodeSet) {
        if (!allowedRemoveNodes.includes(nodeId)) {
          return true;
        }
      }
    }
    return false;
  }
  
  @Selector([
    BulkEditAuthorState.hasNotAllowedCopyNodes$,
    BulkEditAuthorState.hasNotAllowedRemoveNodes$
  ])
  static hasNotAllowedNodeData$(hasNotAllowedCopyNodes: boolean,
                                hasNotAllowedRemoveNodes: boolean) {
    return {
      hasNotAllowedCopyNodes,
      hasNotAllowedRemoveNodes
    };
  }
  
  @Selector([
    BulkEditAuthorState.selectNodeSet$,
    FlatStates.NodeEntityState.typedEntities$,
    FlatStates.PageEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedPage$,
  ])
  static onlySlidesAndTrainings$(selectNodeSet: Set<string>,
                                 typedNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType},
                                 typedPages: { [id: string]: FlatModels.PageEntityModels.PageEntityType},
                                 currentSelectedPage: FlatModels.PageEntityModels.PageEntityType) {
    if (currentSelectedPage
      && (currentSelectedPage.type === FlatModels.PageEntityModels.PageType.enum.Case
        || currentSelectedPage.type === FlatModels.PageEntityModels.PageType.enum.Grid)) {
      for (const nodeId of selectNodeSet) {
        const node = typedNodes[nodeId];
        const page = typedPages[node.page];
        if (page.type !== FlatModels.PageEntityModels.PageType.enum.Slide
          && page.type !== FlatModels.PageEntityModels.PageType.enum.Training) {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  
  @Selector([
    BulkEditAuthorState.hasNotAllowedCopyNodes$,
    BulkEditAuthorState.hasNotAllowedRemoveNodes$,
    BulkEditAuthorState.onlySlidesAndTrainings$,
    BulkEditAuthorState.allowedCopyNodes$,
    BulkEditAuthorState.allowedRemoveNodes$,
  ])
  static hasErrorsNode$(hasNotAllowedCopyNodes: boolean,
                        hasNotAllowedRemoveNodes: boolean,
                        onlySlidesAndTrainings: boolean,
                        allowedCopyNodes: string[],
                        allowedRemoveNodes: string[]) {
    return (id: string) => {
      if (onlySlidesAndTrainings) {
        return false;
      }
      return (hasNotAllowedCopyNodes && hasNotAllowedRemoveNodes)
        || (!allowedCopyNodes.includes(id) && !allowedRemoveNodes.includes(id));
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectNodeSet$,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static nodesVisible$(selectNodeSet: Set<string>,
                       typedEntities: {[id: string]: FlatModels.NodeEntityModels.NodeEntityType}) {
    const nodeArray = Array.from(selectNodeSet);
    let result = true;
    for (const node of nodeArray) {
      const entity = typedEntities[node];
      if (entity && entity.hidden) {
        result = false;
        break;
      }
    }
    return result;
  }
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static copyNodeSet$(state: BulkEditAuthorStateModel,
                      typedNodes: {[id: string]: FlatModels.NodeEntityModels.NodeEntityType}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY) {
      return Array.from(state.copyNodeSet).reduce((arr, node) => {
        if (typedNodes[node]) {
          arr.add(node);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }

  //</editor-fold>
  
  //<editor-fold desc="Page">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.NodeEntityState.typedEntities$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static selectPageSet$(state: BulkEditAuthorStateModel,
                        typedNodes: {[id: string]: FlatModels.NodeEntityModels.NodeEntityType},
                        typedPages: {[id: string]: FlatModels.PageEntityModels.PageEntityType}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY) {
      return Array.from(state.selectNodeSet).reduce((arr, nodeId) => {
        const entity = typedNodes[nodeId];
        if (entity) {
          arr.add(entity.page);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    } else if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY) {
      return Array.from(state.selectPageSet).reduce((arr, page) => {
        if (typedPages[page]) {
          arr.add(page);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }

  @Selector([BulkEditAuthorState.selectPageSet$])
  static hasBulkEditPage$(selectPageSet: Set<string>) {
    return (id: string) => {
      return selectPageSet.has(id);
    }
  }

  @Selector([BulkEditAuthorState.selectPageSet$])
  static hasAllBulkEditPages$(selectPageSet: Set<string>) {
    return (ids: string[]) => {
      let checked = ids.length > 0;
      for (const id of ids) {
        if (!selectPageSet.has(id)) {
          checked = false;
          break;
        }
      }
      return checked;
    };
  }
	
	@Selector([
		BulkEditAuthorState.selectPageSet$,
		FlatStates.PageEntityState.typedEntities$
	])
	static selectPages$(selectPageSet: Set<string>,
	                    typedPages: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
		return Array.from(selectPageSet).reduce((arr, pageId) => {
			const entity = typedPages[pageId];
			if (entity) {
				arr.push(entity as (FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training));
			}
			return arr;
		}, [] as (FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training)[]);
	}
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.NodeEntityState.typedEntities$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static copyPageSet$(state: BulkEditAuthorStateModel,
                      typedNodes: {[id: string]: FlatModels.NodeEntityModels.NodeEntityType},
                      typedPages: {[id: string]: FlatModels.PageEntityModels.PageEntityType}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY) {
      return Array.from(state.copyNodeSet).reduce((arr, node) => {
        const entity = typedNodes[node];
        if (entity) {
          arr.add(entity.page);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    } else if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY) {
      return Array.from(state.copyPageSet).reduce((arr, page) => {
        if (typedPages[page]) {
          arr.add(page);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.copyPageSet$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static copyPages$(copyPageSet: Set<string>,
                    typedPages: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    return Array.from(copyPageSet).reduce((arr, pageId) => {
      const entity = typedPages[pageId];
      if (entity) {
        arr.push(entity as (FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training));
      }
      return arr;
    }, [] as (FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training)[]);
  }

  //</editor-fold>

  //<editor-fold desc="Layer">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.SlideLayerEntityState.typedEntities$
  ])
  static selectLayerSet$(state: BulkEditAuthorStateModel,
                         typedLayers: {[id: string]: FlatModels.LayerEntityModels.Layer}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY) {
      return Array.from(state.selectLayerSet).reduce((arr, layer) => {
        if (typedLayers[layer]) {
          arr.add(layer);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectLayerSet$,
    FlatStates.SlideLayerEntityState.currentSelectedSlideLayers$
  ])
  static currentSelectedSelectLayerSet$(selectLayerSet: Set<string>,
                                        currentSelectedSlideLayers: FlatModels.LayerEntityModels.Layer[]) {
    if (selectLayerSet.size > 0) {
      return currentSelectedSlideLayers.reduce((arr, layer) => {
        if (selectLayerSet.has(layer.id)) {
          arr.add(layer.id);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.SlideLayerEntityState.typedEntities$
  ])
  static copyLayerSet$(state: BulkEditAuthorStateModel,
                       typedLayers: {[id: string]: FlatModels.LayerEntityModels.Layer}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY) {
      return Array.from(state.copyLayerSet).reduce((arr, layer) => {
        if (typedLayers[layer]) {
          arr.add(layer);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([BulkEditAuthorState.selectLayerSet$])
  static hasBulkEditLayer$(layerSet: Set<string>) {
    return (id: string) => {
      return layerSet.has(id);
    }
  }
  
  @Selector([FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$])
  static isLayerCurrent$(currentSelectedSlideLayer: FlatModels.LayerEntityModels.Layer) {
    return (id: string) => {
      return currentSelectedSlideLayer?.id === id;
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectLayerSet$,
    FlatStates.SlideAuthorState.editPanelDisplay$,
    FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$,
  ])
  static isLayerSelected$(layerSet: Set<string>,
                          editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
                          currentSelectedSlideLayer: FlatModels.LayerEntityModels.Layer) {
    return (id: string) => {
      if (layerSet.size === 0) {
        if (editPanelDisplay === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.layer) {
          return currentSelectedSlideLayer?.id === id;
        }
        return false;
      } else {
        return layerSet.has(id);
      }
    }
  }

  //</editor-fold>

  //<editor-fold desc="Image">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.ImageEntityState.typedEntities$
  ])
  static selectImageSet$(state: BulkEditAuthorStateModel,
                         typedImages: {[id: string]: FlatModels.ImageEntityModels.Image}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY) {
      return Array.from(state.selectImageSet).reduce((arr, image) => {
        if (typedImages[image]) {
          arr.add(image);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectImageSet$,
    FlatStates.ImageEntityState.currentSelectedSlideImages$
  ])
  static currentSelectedSelectImageSet$(selectImageSet: Set<string>,
                                        currentSelectedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    if (selectImageSet.size > 0) {
      return currentSelectedSlideImages.reduce((arr, image) => {
        if (selectImageSet.has(image.id)) {
          arr.add(image.id);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.ImageEntityState.typedEntities$
  ])
  static copyImageSet$(state: BulkEditAuthorStateModel,
                       typedImages: {[id: string]: FlatModels.ImageEntityModels.Image}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY) {
      return Array.from(state.copyImageSet).reduce((arr, image) => {
        if (typedImages[image]) {
          arr.add(image);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([BulkEditAuthorState.selectImageSet$])
  static hasBulkEditImage$(imageSet: Set<string>) {
    return (id: string) => {
      return imageSet.has(id);
    }
  }
  
  @Selector([FlatStates.ImageEntityState.currentSelectedSlideImage$])
  static isImageCurrent$(currentSelectedSlideImage: FlatModels.ImageEntityModels.Image) {
    return (id: string) => {
      return currentSelectedSlideImage?.id === id;
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectImageSet$,
    FlatStates.SlideAuthorState.editPanelDisplay$,
    FlatStates.ImageEntityState.currentSelectedSlideImage$,
  ])
  static isImageSelected$(imageSet: Set<string>,
                          editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
                          currentSelectedSlideImage: FlatModels.ImageEntityModels.Image) {
    return (id: string) => {
      if (imageSet.size === 0) {
        if (editPanelDisplay === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image) {
          return currentSelectedSlideImage?.id === id;
        }
        return false;
      } else {
        return imageSet.has(id);
      }
    }
  }

  //</editor-fold>

  //<editor-fold desc="Group">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.AnnotationGroupEntityState.typedEntities$
  ])
  static selectGroupSet$(state: BulkEditAuthorStateModel,
                         typedGroups: {[id: string]: FlatModels.GroupEntityModels.Group}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY) {
      return Array.from(state.selectGroupSet).reduce((arr, group) => {
        if (typedGroups[group]) {
          arr.add(group);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectGroupSet$,
    FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroups$
  ])
  static currentSelectedSelectGroupSet$(selectGroupSet: Set<string>,
                                        currentSelectedSlideGroups: FlatModels.ImageEntityModels.Image[]) {
    if (selectGroupSet.size > 0) {
      return currentSelectedSlideGroups.reduce((arr, group) => {
        if (selectGroupSet.has(group.id)) {
          arr.add(group.id);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }

  @Selector([
    BulkEditAuthorState,
    FlatStates.AnnotationGroupEntityState.typedEntities$
  ])
  static copyGroupSet$(state: BulkEditAuthorStateModel,
                       typedGroups: {[id: string]: FlatModels.GroupEntityModels.Group}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY) {
      return Array.from(state.copyGroupSet).reduce((arr, group) => {
        if (typedGroups[group]) {
          arr.add(group);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([BulkEditAuthorState.selectGroupSet$])
  static hasBulkEditGroup$(groupSet: Set<string>) {
    return (id: string) => {
      return groupSet.has(id);
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectGroupSet$,
    FlatStates.SlideAuthorState.editPanelDisplay$,
    FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroup$,
  ])
  static isGroupSelected$(groupSet: Set<string>,
                          editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
                          currentSelectedSlideGroup: FlatModels.GroupEntityModels.Group) {
    return (id: string) => {
      if (groupSet.size === 0) {
        if (editPanelDisplay === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.group) {
          return currentSelectedSlideGroup?.id === id;
        }
        return false;
      } else {
        return groupSet.has(id);
      }
    }
  }

  //</editor-fold>

  //<editor-fold desc="Annotation">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.AnnotationEntityState.typedEntities$
  ])
  static selectAnnotationSet$(state: BulkEditAuthorStateModel,
                              typedAnnotations: {[id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY) {
      return Array.from(state.selectAnnotationSet).reduce((arr, annotation) => {
        if (typedAnnotations[annotation]) {
          arr.add(annotation);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectAnnotationSet$,
    FlatStates.AnnotationEntityState.currentSelectedSlideAnnotations$
  ])
  static currentSelectedSelectAnnotationSet$(selectAnnotationSet: Set<string>,
                                             currentSelectedSlideAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[]) {
    if (selectAnnotationSet.size > 0) {
      return currentSelectedSlideAnnotations.reduce((arr, annotation) => {
        if (selectAnnotationSet.has(annotation.id)) {
          arr.add(annotation.id);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }

  @Selector([
    BulkEditAuthorState,
    FlatStates.AnnotationEntityState.typedEntities$
  ])
  static copyAnnotationSet$(state: BulkEditAuthorStateModel,
                            typedAnnotations: {[id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY) {
      return Array.from(state.copyAnnotationSet).reduce((arr, annotation) => {
        if (typedAnnotations[annotation]) {
          arr.add(annotation);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([BulkEditAuthorState.selectAnnotationSet$])
  static hasBulkEditAnnotation$(annotationSet: Set<string>) {
    return (id: string) => {
      return annotationSet.has(id);
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectAnnotationSet$,
    FlatStates.SlideAuthorState.editPanelDisplay$,
    FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$,
  ])
  static isAnnotationSelected$(annotationSet: Set<string>,
                               editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
                               currentSelectedSlideAnnotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    return (id: string) => {
      if (annotationSet.size === 0) {
        if (editPanelDisplay === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation) {
          return currentSelectedSlideAnnotation?.id === id;
        }
        return false;
      } else {
        return annotationSet.has(id);
      }
    }
  }

  //</editor-fold>

  //<editor-fold desc="Question">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.QuestionEntityState.typedEntities$
  ])
  static selectQuestionSet$(state: BulkEditAuthorStateModel,
                            typedQuestions: {[id: string]: FlatModels.QuestionEntityModels.QuestionEntityType}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY) {
      return Array.from(state.selectQuestionSet).reduce((arr, question) => {
        if (typedQuestions[question]) {
          arr.add(question);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectQuestionSet$,
    FlatStates.QuestionEntityState.currentSelectedSlideQuestions$
  ])
  static currentSelectedSelectQuestionSet$(selectQuestionSet: Set<string>,
                                           currentSelectedSlideQuestions: FlatModels.QuestionEntityModels.QuestionEntityType[]) {
    if (selectQuestionSet.size > 0) {
      return currentSelectedSlideQuestions.reduce((arr, question) => {
        if (selectQuestionSet.has(question.id)) {
          arr.add(question.id);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }

  @Selector([
    BulkEditAuthorState,
    FlatStates.QuestionEntityState.typedEntities$
  ])
  static copyQuestionSet$(state: BulkEditAuthorStateModel,
                          typedQuestions: {[id: string]: FlatModels.QuestionEntityModels.QuestionEntityType}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY) {
      return Array.from(state.copyQuestionSet).reduce((arr, question) => {
        if (typedQuestions[question]) {
          arr.add(question);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([BulkEditAuthorState.selectQuestionSet$])
  static hasBulkEditQuestion$(questionSet: Set<string>) {
    return (id: string) => {
      return questionSet.has(id);
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectQuestionSet$,
    FlatStates.SlideAuthorState.editPanelDisplay$,
    FlatStates.QuestionEntityState.currentSelectedSlideQuestion$,
  ])
  static isQuestionSelected$(questionSet: Set<string>,
                             editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
                             currentSelectedSlideQuestion: FlatModels.QuestionEntityModels.QuestionEntityType) {
    return (id: string) => {
      if (questionSet.size === 0) {
        if (editPanelDisplay === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question) {
          return currentSelectedSlideQuestion?.id === id;
        }
        return false;
      } else {
        return questionSet.has(id);
      }
    }
  }

  //</editor-fold>
  
  //<editor-fold desc="Poi">
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.PointOfInterestEntityState.typedEntities$
  ])
  static selectPoiSet$(state: BulkEditAuthorStateModel,
                       typedPois: {[id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest}) {
    if (state.selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY) {
      return Array.from(state.selectPoiSet).reduce((arr, poi) => {
        if (typedPois[poi]) {
          arr.add(poi);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState.selectPoiSet$,
    FlatStates.PointOfInterestEntityState.currentSelectedSlidePois$
  ])
  static currentSelectedSelectPoiSet$(selectPoiSet: Set<string>,
                                      currentSelectedSlidePois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    if (selectPoiSet.size > 0) {
      return currentSelectedSlidePois.reduce((arr, poi) => {
        if (selectPoiSet.has(poi.id)) {
          arr.add(poi.id);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([
    BulkEditAuthorState,
    FlatStates.PointOfInterestEntityState.typedEntities$
  ])
  static copyPoiSet$(state: BulkEditAuthorStateModel,
                     typedPois: {[id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest}) {
    if (state.copyType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY) {
      return Array.from(state.copyPoiSet).reduce((arr, poi) => {
        if (typedPois[poi]) {
          arr.add(poi);
        }
        return arr;
      }, new Set<string>()) as Set<string>;
    }
    return new Set<string>();
  }
  
  @Selector([BulkEditAuthorState.selectPoiSet$])
  static hasBulkEditPoi$(poiSet: Set<string>) {
    return (id: string) => {
      return poiSet.has(id);
    }
  }
  
  @Selector([
    BulkEditAuthorState.selectPoiSet$,
    FlatStates.SlideAuthorState.editPanelDisplay$,
    FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$,
  ])
  static isPoiSelected$(poiSet: Set<string>,
                        editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
                        currentSelectedSlidePoi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    return (id: string) => {
      if (poiSet.size === 0) {
        if (editPanelDisplay === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.poi) {
          return currentSelectedSlidePoi?.id === id;
        }
        return false;
      } else {
        return poiSet.has(id);
      }
    }
  }
  
  //</editor-fold>
  
  @Selector([
    BulkEditAuthorState.currentSelectedSelectLayerSet$,
    BulkEditAuthorState.selectImageSet$,
    BulkEditAuthorState.selectGroupSet$,
    BulkEditAuthorState.currentSelectedSelectAnnotationSet$,
    BulkEditAuthorState.currentSelectedSelectQuestionSet$,
    BulkEditAuthorState.currentSelectedSelectPoiSet$,
  ])
  static slideBulkEditItemCount$(selectLayerSet: Set<string>,
                                 selectImageSet: Set<string>,
                                 selectGroupSet: Set<string>,
                                 selectAnnotationSet: Set<string>,
                                 selectQuestionSet: Set<string>,
                                 selectPoiSet: Set<string>) {
    return selectLayerSet.size + selectImageSet.size + selectGroupSet.size
      + selectAnnotationSet.size + selectQuestionSet.size + selectPoiSet.size;
  }
  
  @Selector([
    BulkEditAuthorState.copyLayerSet$,
    BulkEditAuthorState.copyImageSet$,
    BulkEditAuthorState.copyGroupSet$,
    BulkEditAuthorState.copyAnnotationSet$,
    BulkEditAuthorState.copyQuestionSet$,
    BulkEditAuthorState.copyPoiSet$,
  ])
  static slideBulkCopyItemCount$(copyLayerSet: Set<string>,
                                 copyImageSet: Set<string>,
                                 copyGroupSet: Set<string>,
                                 copyAnnotationSet: Set<string>,
                                 copyQuestionSet: Set<string>,
                                 copyPoiSet: Set<string>) {
    return copyLayerSet.size + copyImageSet.size + copyGroupSet.size
      + copyAnnotationSet.size + copyQuestionSet.size + copyPoiSet.size;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">
  
  @Action(BulkEditAuthorActions.Reset)
  reset({patchState}: StateContext<BulkEditAuthorStateModel>) {
    // Patch State
    patchState({
      selectType: null,
      copyType: null,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
      copyMediaSet: new Set<string>(),
      copyNodeSet: new Set<string>(),
      copyPageSet: new Set<string>(),
      copyLayerSet: new Set<string>(),
      copyImageSet: new Set<string>(),
      copyGroupSet: new Set<string>(),
      copyAnnotationSet: new Set<string>(),
      copyQuestionSet: new Set<string>(),
      copyChoiceSet: new Set<string>(),
      copyPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.ResetSelection)
  resetSelection({patchState}: StateContext<BulkEditAuthorStateModel>) {
    // Patch State
    patchState({
      selectType: null,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  //<editor-fold desc="Media">
  
  @Action(BulkEditAuthorActions.ToggleMedia)
  toggleMedia({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.ToggleMedia) {
    // Get Media Set
    let mediaSet = structuredClone(getState().selectMediaSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Check For Current Media
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.MEDIA_ENTITY) {
      mediaSet = new Set<string>();
      const currentSelectedSlideMedia = this.store.selectSnapshot(FlatStates.MediaEntityState.currentSelectedSlideMedia$);
      if (currentSelectedSlideMedia) {
        mediaSet.add(currentSelectedSlideMedia.id);
      }
    }
    
    // Update Media Set
    if (mediaSet.has(id) && selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.MEDIA_ENTITY) {
      mediaSet.delete(id);
    } else {
      mediaSet.add(id);
    }
    
    // PatchState
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.MEDIA_ENTITY,
      selectMediaSet: mediaSet,
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetMedias)
  setMedias({patchState}: StateContext<BulkEditAuthorStateModel>,
            {ids}: BulkEditAuthorActions.SetMedias) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.MEDIA_ENTITY,
      selectMediaSet: new Set(ids),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveMedia)
  removeMedia({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.RemoveMedia) {
    // Get Media Set
    const mediaSet = structuredClone(getState().selectMediaSet);
    
    // Update Media
    mediaSet.delete(id);
    
    // Patch State
    patchState({
      selectMediaSet: mediaSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveMedias)
  removeMedias({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
               {ids}: BulkEditAuthorActions.RemoveMedias) {
    // Get Media Set
    const mediaSet = structuredClone(getState().selectMediaSet);
    
    // Update Media
    for (const id of ids) {
      mediaSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectMediaSet: mediaSet,
    });
  }

  //</editor-fold>
  
  //<editor-fold desc="Node">
  
  @Action(BulkEditAuthorActions.ToggleNode)
  toggleNode({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
             {id, parentId}: BulkEditAuthorActions.ToggleNode) {
    // Get Node Set
    let nodeSet = structuredClone(getState().selectNodeSet);
    
    // Check Parent Node Id
    if (getState().parentNodeId !== parentId) {
      nodeSet = new Set<string>();
    } else {
      // Get Select Type
      const selectType = getState().selectType;
      
      // Check For Current Node
      if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY) {
        nodeSet = new Set<string>();
        const currentSelectedSlideNode = this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedSlideNode$);
        if (currentSelectedSlideNode) {
          nodeSet.add(currentSelectedSlideNode.id);
        }
      }
    }
    
    // Update Node Set
    if (nodeSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY) {
      nodeSet.delete(id);
    } else {
      nodeSet.add(id);
    }
    
    // PatchState
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY,
      parentNodeId: parentId,
      selectNodeSet: nodeSet,
      selectMediaSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetNodes)
  setNodes({patchState}: StateContext<BulkEditAuthorStateModel>,
           {ids}: BulkEditAuthorActions.SetNodes) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY,
      selectNodeSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyNodes)
  copyNodes({patchState}: StateContext<BulkEditAuthorStateModel>,
            {selectNodeSet}: BulkEditAuthorActions.CopyNodes) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.NODE_ENTITY,
      copyNodeSet: selectNodeSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveNode)
  removeNode({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
             {id}: BulkEditAuthorActions.RemoveNode) {
    // Get Node Set
    const nodeSet = structuredClone(getState().selectNodeSet);
    
    // Update Node
    nodeSet.delete(id);
    
    // Patch State
    patchState({
      selectNodeSet: nodeSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveNodes)
  removeNodes({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {ids}: BulkEditAuthorActions.RemoveNodes) {
    // Get Node Set
    const nodeSet = structuredClone(getState().selectNodeSet);
    
    // Update Node
    for (const id of ids) {
      nodeSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectNodeSet: nodeSet,
    });
  }

  //</editor-fold>
  
  //<editor-fold desc="Page">
  
  @Action(BulkEditAuthorActions.TogglePage)
  togglePage({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
             {id}: BulkEditAuthorActions.TogglePage) {
    // Get Page Set
    let pageSet = structuredClone(getState().selectPageSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Check For Current Page
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY) {
      pageSet = new Set<string>();
      const currentSelectedSlidePage = this.store.selectSnapshot(FlatStates.PageEntityState.currentSelectedSlidePage$);
      if (currentSelectedSlidePage) {
        pageSet.add(currentSelectedSlidePage.id);
      }
    }
    
    // Update Page Set
    if (pageSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY) {
      pageSet.delete(id);
    } else {
      pageSet.add(id);
    }
    
    // PatchState
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY,
      selectPageSet: pageSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetPages)
  setPages({patchState}: StateContext<BulkEditAuthorStateModel>,
           {ids}: BulkEditAuthorActions.SetPages) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY,
      selectPageSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyPages)
  copyPages({patchState}: StateContext<BulkEditAuthorStateModel>,
            {selectPageSet}: BulkEditAuthorActions.CopyPages) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.PAGE_ENTITY,
      copyPageSet: selectPageSet,
    });
  }
  
  @Action(BulkEditAuthorActions.SetCopyPages)
  setCopyPages({patchState}: StateContext<BulkEditAuthorStateModel>,
               {copyPageSet}: BulkEditAuthorActions.SetCopyPages) {
    // Patch State
    patchState({
      copyPageSet
    });
  }
  
  @Action(BulkEditAuthorActions.RemovePage)
  removePage({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
             {id}: BulkEditAuthorActions.RemovePage) {
    // Get Page Set
    const pageSet = structuredClone(getState().selectPageSet);
    
    // Update Page
    pageSet.delete(id);
    
    // Patch State
    patchState({
      selectPageSet: pageSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemovePages)
  removePages({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {ids}: BulkEditAuthorActions.RemovePages) {
    // Get Page Set
    const pageSet = structuredClone(getState().selectPageSet);
    
    // Update Page
    for (const id of ids) {
      pageSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectPageSet: pageSet,
    });
  }

  //</editor-fold>

  //<editor-fold desc="Layer">
  
  @Action(BulkEditAuthorActions.ToggleLayer)
  toggleLayer({getState, patchState, dispatch}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.ToggleLayer) {
    // Get Layer Set
    let layerSet = structuredClone(getState().selectLayerSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Get Current Selected Slide Layer
    const currentSelectedSlideLayer = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$);
    
    // Check For Current Layer
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY) {
      layerSet = new Set<string>();
      if (currentSelectedSlideLayer) {
        layerSet.add(currentSelectedSlideLayer.id);
      }
    }
    
    // Update Layer Set
    if (layerSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY) {
      layerSet.delete(id);
    } else {
      layerSet.add(id);
    }
    
    // Check For Empty
    let newSelectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null = FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY;
    const editPanelOverlay = this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
    if (!editPanelOverlay) {
      if (layerSet.size === 0) {
        newSelectType = null;
        this.#setSelectedEditPanelDisplay(dispatch);
      } else {
        dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.layer, true));
      }
    }
    
    // PatchState
    patchState({
      selectType: newSelectType,
      selectLayerSet: layerSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetLayers)
  setLayers({patchState}: StateContext<BulkEditAuthorStateModel>,
            {ids}: BulkEditAuthorActions.SetLayers) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY,
      selectLayerSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyLayers)
  copyLayers({patchState}: StateContext<BulkEditAuthorStateModel>,
             {selectLayerSet}: BulkEditAuthorActions.CopyLayers) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.LAYER_ENTITY,
      copyLayerSet: selectLayerSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveLayer)
  removeLayer({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.RemoveLayer) {
    // Get Layer Set
    const layerSet = structuredClone(getState().selectLayerSet);
    
    // Update Layer
    layerSet.delete(id);
    
    // Patch State
    patchState({
      selectLayerSet: layerSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveLayers)
  removeLayers({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
               {ids}: BulkEditAuthorActions.RemoveLayers) {
    // Get Layer Set
    const layerSet = structuredClone(getState().selectLayerSet);
    
    // Update Layer
    for (const id of ids) {
      layerSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectLayerSet: layerSet,
    });
  }

  //</editor-fold>

  //<editor-fold desc="Image">
  
  @Action(BulkEditAuthorActions.ToggleImage)
  toggleImage({getState, patchState, dispatch}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.ToggleImage) {
    // Get Image Set
    let imageSet = structuredClone(getState().selectImageSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Get Current Selected Slide Image
    const currentSelectedSlideImage = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$);
    
    // Check For Current Image
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY) {
      imageSet = new Set<string>();
      if (currentSelectedSlideImage) {
        imageSet.add(currentSelectedSlideImage.id);
      }
    }
    
    // Update Image Set
    if (imageSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY) {
      imageSet.delete(id);
    } else {
      imageSet.add(id);
    }
    
    // Check For Empty
    let newSelectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null = FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY;
    const editPanelOverlay = this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
    if (!editPanelOverlay) {
      if (imageSet.size === 0) {
        newSelectType = null;
        this.#setSelectedEditPanelDisplay(dispatch);
      } else {
        dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image, true));
      }
    }
    
    // PatchState
    patchState({
      selectType: newSelectType,
      selectImageSet: imageSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetImages)
  setImages({patchState}: StateContext<BulkEditAuthorStateModel>,
            {ids}: BulkEditAuthorActions.SetImages) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY,
      selectImageSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyImages)
  copyImages({patchState}: StateContext<BulkEditAuthorStateModel>,
             {selectImageSet}: BulkEditAuthorActions.CopyImages) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.IMAGE_ENTITY,
      copyImageSet: selectImageSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveImage)
  removeImage({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.RemoveImage) {
    // Get Image Set
    const imageSet = structuredClone(getState().selectImageSet);
    
    // Update Image
    imageSet.delete(id);
    
    // Patch State
    patchState({
      selectImageSet: imageSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveImages)
  removeImages({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
               {ids}: BulkEditAuthorActions.RemoveImages) {
    // Get Image Set
    const imageSet = structuredClone(getState().selectImageSet);
    
    // Update Image
    for (const id of ids) {
      imageSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectImageSet: imageSet,
    });
  }

  //</editor-fold>
  
  //<editor-fold desc="Group">
  
  @Action(BulkEditAuthorActions.ToggleGroup)
  toggleGroup({getState, patchState, dispatch}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.ToggleGroup) {
    // Get Group Set
    let groupSet = structuredClone(getState().selectGroupSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Get Current Selected Slide Group
    const currentSelectedSlideGroup = this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroup$);
    
    // Check For Current Group
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY) {
      groupSet = new Set<string>();
      if (currentSelectedSlideGroup) {
        groupSet.add(currentSelectedSlideGroup.id);
      }
    }
    
    // Update Group Set
    if (groupSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY) {
      groupSet.delete(id);
    } else {
      groupSet.add(id);
    }
    
    // Check For Empty
    let newSelectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null = FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY;
    const editPanelOverlay = this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
    if (!editPanelOverlay) {
      if (groupSet.size === 0) {
        newSelectType = null;
        this.#setSelectedEditPanelDisplay(dispatch);
      } else {
        dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.group, true));
      }
    }
    
    // PatchState
    patchState({
      selectType: newSelectType,
      selectGroupSet: groupSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetGroups)
  setGroups({patchState}: StateContext<BulkEditAuthorStateModel>,
            {ids}: BulkEditAuthorActions.SetGroups) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY,
      selectGroupSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyGroups)
  copyGroups({patchState}: StateContext<BulkEditAuthorStateModel>,
             {selectGroupSet}: BulkEditAuthorActions.CopyGroups) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_GROUP_ENTITY,
      copyGroupSet: selectGroupSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveGroup)
  removeGroup({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
              {id}: BulkEditAuthorActions.RemoveGroup) {
    // Get Group Set
    const groupSet = structuredClone(getState().selectGroupSet);
    
    // Update Group
    groupSet.delete(id);
    
    // Patch State
    patchState({
      selectGroupSet: groupSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveGroups)
  removeGroups({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
               {ids}: BulkEditAuthorActions.RemoveGroups) {
    // Get Group Set
    const groupSet = structuredClone(getState().selectGroupSet);
    
    // Update Group
    for (const id of ids) {
      groupSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectGroupSet: groupSet,
    });
  }

  //</editor-fold>

  //<editor-fold desc="Annotation">
  
  @Action(BulkEditAuthorActions.ToggleAnnotation)
  toggleAnnotation({getState, patchState, dispatch}: StateContext<BulkEditAuthorStateModel>,
                   {id}: BulkEditAuthorActions.ToggleAnnotation) {
    // Get Annotation Set
    let annotationSet = structuredClone(getState().selectAnnotationSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Get Current Selected Slide Annotation
    const currentSelectedSlideAnnotation = this.store.selectSnapshot(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$);
    
    // Check For Current Annotation
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY) {
      annotationSet = new Set<string>();
      if (currentSelectedSlideAnnotation) {
        annotationSet.add(currentSelectedSlideAnnotation.id);
      }
    }
    
    // Update Annotation Set
    if (annotationSet.has(id) && selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY) {
      annotationSet.delete(id);
    } else {
      annotationSet.add(id);
    }
    
    // Check For Empty
    let newSelectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null = FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY;
    const editPanelOverlay = this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
    if (!editPanelOverlay) {
      if (annotationSet.size === 0) {
        newSelectType = null;
        this.#setSelectedEditPanelDisplay(dispatch);
      } else {
        dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation, true));
      }
    }
    
    // PatchState
    patchState({
      selectType: newSelectType,
      selectAnnotationSet: annotationSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetAnnotations)
  setAnnotations({patchState}: StateContext<BulkEditAuthorStateModel>,
                 {ids}: BulkEditAuthorActions.SetAnnotations) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY,
      selectAnnotationSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyAnnotations)
  copyAnnotations({patchState}: StateContext<BulkEditAuthorStateModel>,
                  {selectAnnotationSet}: BulkEditAuthorActions.CopyAnnotations) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.ANNOTATION_ENTITY,
      copyAnnotationSet: selectAnnotationSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveAnnotation)
  removeAnnotation({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
                   {id}: BulkEditAuthorActions.RemoveAnnotation) {
    // Get Annotation Set
    const annotationSet = structuredClone(getState().selectAnnotationSet);
    
    // Update Annotation
    annotationSet.delete(id);
    
    // Patch State
    patchState({
      selectAnnotationSet: annotationSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveAnnotations)
  removeAnnotations({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
                    {ids}: BulkEditAuthorActions.RemoveAnnotations) {
    // Get Annotation Set
    const annotationSet = structuredClone(getState().selectAnnotationSet);
    
    // Update Annotation
    for (const id of ids) {
      annotationSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectAnnotationSet: annotationSet,
    });
  }

  //</editor-fold>

  //<editor-fold desc="Question">
  
  @Action(BulkEditAuthorActions.ToggleQuestion)
  toggleQuestion({getState, patchState, dispatch}: StateContext<BulkEditAuthorStateModel>,
                 {id}: BulkEditAuthorActions.ToggleQuestion) {
    // Get Question Set
    let questionSet = structuredClone(getState().selectQuestionSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Get Current Selected Slide Question
    const currentSelectedSlideQuestion = this.store.selectSnapshot(FlatStates.QuestionEntityState.currentSelectedSlideQuestion$);
    
    // Check For Current Question
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY) {
      questionSet = new Set<string>();
      if (currentSelectedSlideQuestion) {
        questionSet.add(currentSelectedSlideQuestion.id);
      }
    }
    
    // Update Question Set
    if (questionSet.has(id) && selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY) {
      questionSet.delete(id);
    } else {
      questionSet.add(id);
    }
    
    // Check For Empty
    let newSelectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null = FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY;
    const editPanelOverlay = this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
    if (!editPanelOverlay) {
      if (questionSet.size === 0) {
        newSelectType = null;
        this.#setSelectedEditPanelDisplay(dispatch);
      } else {
        dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question, true));
      }
    }
    
    // PatchState
    patchState({
      selectType: newSelectType,
      selectQuestionSet: questionSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetQuestions)
  setQuestions({patchState}: StateContext<BulkEditAuthorStateModel>,
               {ids}: BulkEditAuthorActions.SetQuestions) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY,
      selectQuestionSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyQuestions)
  copyQuestions({patchState}: StateContext<BulkEditAuthorStateModel>,
                {selectQuestionSet}: BulkEditAuthorActions.CopyQuestions) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.QUESTION_ENTITY,
      copyQuestionSet: selectQuestionSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveQuestion)
  removeQuestion({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
                 {id}: BulkEditAuthorActions.RemoveQuestion) {
    // Get Question Set
    const questionSet = structuredClone(getState().selectQuestionSet);
    
    // Update Question
    questionSet.delete(id);
    
    // Patch State
    patchState({
      selectQuestionSet: questionSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveQuestions)
  removeQuestions({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
                  {ids}: BulkEditAuthorActions.RemoveQuestions) {
    // Get Question Set
    const questionSet = structuredClone(getState().selectQuestionSet);
    
    // Update Question
    for (const id of ids) {
      questionSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectQuestionSet: questionSet,
    });
  }

  //</editor-fold>

  //<editor-fold desc="Choice">
  
  @Action(BulkEditAuthorActions.ToggleChoice)
  toggleChoice({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
               {id}: BulkEditAuthorActions.ToggleChoice) {
    // Get Choice Set
    let choiceSet = structuredClone(getState().selectChoiceSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Check For Current Poi
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.CHOICE_ENTITY) {
      choiceSet = new Set<string>();
    }
    
    // Update Choice Set
    if (choiceSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.CHOICE_ENTITY) {
      choiceSet.delete(id);
    } else {
      choiceSet.add(id);
    }
    
    // PatchState
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.CHOICE_ENTITY,
      selectChoiceSet: choiceSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetChoices)
  setChoices({patchState}: StateContext<BulkEditAuthorStateModel>,
             {ids}: BulkEditAuthorActions.SetChoices) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.CHOICE_ENTITY,
      selectChoiceSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectPoiSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveChoice)
  removeChoice({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
               {id}: BulkEditAuthorActions.RemoveChoice) {
    // Get Choice Set
    const choiceSet = structuredClone(getState().selectChoiceSet);
    
    // Update Choice
    choiceSet.delete(id);
    
    // Patch State
    patchState({
      selectChoiceSet: choiceSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemoveChoices)
  removeChoices({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
                {ids}: BulkEditAuthorActions.RemoveChoices) {
    // Get Choice Set
    const choiceSet = structuredClone(getState().selectChoiceSet);
    
    // Update Choice
    for (const id of ids) {
      choiceSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectChoiceSet: choiceSet,
    });
  }

  //</editor-fold>
  
  //<editor-fold desc="Poi">
  
  @Action(BulkEditAuthorActions.TogglePoi)
  togglePoi({getState, patchState, dispatch}: StateContext<BulkEditAuthorStateModel>,
            {id}: BulkEditAuthorActions.TogglePoi) {
    // Get Poi Set
    let poiSet = structuredClone(getState().selectPoiSet);
    
    // Get Select Type
    const selectType = getState().selectType;
    
    // Get Current Selected Slide Poi
    const currentSelectedSlidePoi = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$);
    
    // Check For Current Poi
    if (selectType !== FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY) {
      poiSet = new Set<string>();
      if (currentSelectedSlidePoi) {
        poiSet.add(currentSelectedSlidePoi.id);
      }
    }
    
    // Update Poi Set
    if (poiSet.has(id) && getState().selectType === FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY) {
      poiSet.delete(id);
    } else {
      poiSet.add(id);
    }
    
    // Check For Empty
    let newSelectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType | null = FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY;
    const editPanelOverlay = this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
    if (!editPanelOverlay) {
      if (poiSet.size === 0) {
        newSelectType = null;
        this.#setSelectedEditPanelDisplay(dispatch);
      } else {
        dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.poi, true));
      }
    }
    
    // Patch State
    patchState({
      selectType: newSelectType,
      selectPoiSet: poiSet,
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.SetPois)
  setPois({patchState}: StateContext<BulkEditAuthorStateModel>,
          {ids}: BulkEditAuthorActions.SetPois) {
    // Patch State
    patchState({
      selectType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY,
      selectPoiSet: new Set(ids),
      selectMediaSet: new Set<string>(),
      parentNodeId: null,
      selectNodeSet: new Set<string>(),
      selectPageSet: new Set<string>(),
      selectLayerSet: new Set<string>(),
      selectImageSet: new Set<string>(),
      selectGroupSet: new Set<string>(),
      selectAnnotationSet: new Set<string>(),
      selectQuestionSet: new Set<string>(),
      selectChoiceSet: new Set<string>(),
    });
  }
  
  @Action(BulkEditAuthorActions.CopyPois)
  copyPois({patchState}: StateContext<BulkEditAuthorStateModel>,
           {selectPoiSet}: BulkEditAuthorActions.CopyPois) {
    // Patch State
    patchState({
      copyType: FlatModels.BulkEditAuthorModels.BulkEditEntityType.enum.POINT_OF_INTEREST_ENTITY,
      copyPoiSet: selectPoiSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemovePoi)
  removePoi({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
            {id}: BulkEditAuthorActions.RemovePoi) {
    // Get Poi Set
    const poiSet = structuredClone(getState().selectPoiSet);
    
    // Update Poi
    poiSet.delete(id);
    
    // Patch State
    patchState({
      selectPoiSet: poiSet,
    });
  }
  
  @Action(BulkEditAuthorActions.RemovePois)
  removePois({getState, patchState}: StateContext<BulkEditAuthorStateModel>,
             {ids}: BulkEditAuthorActions.RemovePois) {
    // Get Poi Set
    const poiSet = structuredClone(getState().selectPoiSet);
    
    // Update Poi
    for (const id of ids) {
      poiSet.delete(id);
    }
    
    // Patch State
    patchState({
      selectPoiSet: poiSet,
    });
  }
  
  //</editor-fold>
  
  #setSelectedEditPanelDisplay(dispatch: (actions: any) => Observable<void>) {
    // Get Current Selected Question
    const currentSelectedSlideQuestion = this.store.selectSnapshot(FlatStates.QuestionEntityState.currentSelectedSlideQuestion$);
    if (currentSelectedSlideQuestion) {
      return dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question, false));
    }
    
    // Get Current Selected Group
    const currentSelectedSlideGroup = this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroup$);
    if (currentSelectedSlideGroup) {
      return dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.group, false));
    }
    
    // Get Current Selected Poi
    const currentSelectedSlidePoi = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$);
    if (currentSelectedSlidePoi) {
      return dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.poi, false));
    }
    
    // Get Current Selected Slide Annotation
    const currentSelectedSlideAnnotation = this.store.selectSnapshot(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$);
    if (currentSelectedSlideAnnotation) {
      return dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation, false));
    }
    
    // Get Current Selected Slide Image
    const currentSelectedSlideImage = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$);
    if (currentSelectedSlideImage) {
      return dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image, false));
    }
    
    return dispatch(new SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.layer, false));
  }

  //</editor-fold>
}
